import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const Footer = ({ data, isHome = false }) => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null);
  const navigate = useNavigate();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeStatus(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Store in localStorage for demo purposes
      const subscribers = JSON.parse(localStorage.getItem("newsletterSubscribers") || "[]");
      subscribers.push({
        email,
        timestamp: new Date().toISOString(),
        id: Date.now()
      });
      localStorage.setItem("newsletterSubscribers", JSON.stringify(subscribers));
      
      setSubscribeStatus("success");
      setEmail("");
    } catch (error) {
      setSubscribeStatus("error");
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isHome) {
      // Scroll to top on homepage
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    } else {
      // Navigate to homepage from other pages
      navigate('/');
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-column">
            <div className="footer-logo">
              <a href="/" className="footer-logo-link" onClick={handleLogoClick}>
                <img 
                  src="/atsomnium-logo-wht.svg"
                  alt="ATSOMNIUM"
                  width="140"
                  height="auto"
                  className="footer-logo-img"
                />
              </a>
            </div>
            <p className="footer-description">
              {data.description}
            </p>
            <div className="footer-social">
              {data.socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  className="social-link"
                  aria-label={`Visit our ${link.platform}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    {link.icon}
                  </svg>
                </a>
              ))}
            </div>
          </div>

          <div className="footer-column">
            <h3 className="footer-title">Latest Insights</h3>
            {data.latestPost ? (
              <article className="blog-preview">
                <div className="blog-preview-image">
                  <img
                    src="https://images.unsplash.com/photo-1506787497326-c2736dde1bef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxidXNpbmVzc3xlbnwwfHx8YmxhY2tfYW5kX3doaXRlfDE3NTI0NDU2NTl8MA&ixlib=rb-4.1.0&q=85"
                    alt={data.latestPost.title}
                  />
                </div>
                <div className="blog-preview-content">
                  <h4 className="blog-preview-title">
                    <a href={`/news/${data.latestPost.slug}`}>
                      {data.latestPost.title}
                    </a>
                  </h4>
                  <p className="blog-preview-excerpt">
                    {data.latestPost.excerpt}
                  </p>
                  <time className="blog-preview-date" dateTime={data.latestPost.date}>
                    {new Date(data.latestPost.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </time>
                </div>
              </article>
            ) : (
              <p className="footer-description">Loading latest insights...</p>
            )}
          </div>

          <div className="footer-column">
            <h3 className="footer-title">Stay Updated</h3>
            <p className="newsletter-description">
              Get the latest insights on design, development, and digital trends 
              delivered to your inbox.
            </p>
            
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <div className="newsletter-input-group">
                <label htmlFor="newsletter-email" className="sr-only">
                  Email address for newsletter
                </label>
                <input
                  type="email"
                  id="newsletter-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="newsletter-input"
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="newsletter-submit"
                  aria-label="Subscribe to newsletter"
                >
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="m3 3 18 9-18 9v-6l12-3-12-3z"/>
                  </svg>
                </button>
              </div>
              
              {subscribeStatus === "success" && (
                <div className="newsletter-success" role="status" aria-live="polite">
                  Thanks for subscribing!
                </div>
              )}
              
              {subscribeStatus === "error" && (
                <div className="newsletter-error" role="alert" aria-live="assertive">
                  Please try again.
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">
            © {new Date().getFullYear()} ATSOMNIUM. All rights reserved.
          </p>
          <div className="footer-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;